// src/api/axios.js
import axios from "axios";
import config from "../config";
import { triggerAuthFail } from "./authEvents";

const api = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true, // send httpOnly cookies
});

/**
 * Single-flight refresh lock (prevents multiple refresh calls)
 */
let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;

    // Prevent retry loop and ignore refresh-token request itself
    const isRefreshRequest = originalRequest?.url?.includes(
      "/cauthRoutes/customer/refresh-token"
    );

    const shouldTryRefresh =
      (status === 401 || status === 403) &&
      !originalRequest._retry &&
      !isRefreshRequest;

    if (!shouldTryRefresh) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If a refresh is already happening, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: () => resolve(api(originalRequest)),
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      await api.post("/cauthRoutes/customer/refresh-token");
      processQueue(null);
      return api(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr);

      // ✅ Force UI logout immediately (no ghost session)
      triggerAuthFail();

      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;